// ─── AOPetrotecOrderSection — bloco "Petrotec Angola" no OrderForm ────────
//
// Renderiza apenas se o operador estiver em modo AO E o tipo de OT for
// 'assistance'. Em qualquer outro caso devolve null e não tem efeito no
// fluxo PT (Petrotec Portugal e restantes tenants).
//
// Fornece pickers em cascata:
//   Cliente Final → Posto → Tipo de Intervenção → Equipamentos (multi)
//
// Quando o operador escolhe um Posto com coords/morada definidas, dispara
// `onSiteSelected({locationName, address, coordinates})` para o OrderForm
// auto-popular o bloco de "entrega" (que para assistência é o local do
// serviço). O operador pode editar à mão depois — não impomos a coord do
// posto, apenas a sugerimos.
//
// O selo do SLA actual (em vigor para o cliente final × tipo) aparece
// assim que cliente e tipo estiverem definidos — útil para o operador
// saber o relógio que está a correr.

import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import {
  Box, Typography, Select, MenuItem, FormControl, InputLabel, Chip,
  CircularProgress, IconButton, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import BusinessIcon from '@material-ui/icons/Business';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import BuildIcon from '@material-ui/icons/Build';
import TimerIcon from '@material-ui/icons/Timer';
// (BuildIcon usado tanto no header da secção como no select de Tipo)
import { AuthContext } from '../../App';
import { isPetrotecTenant } from '../../utils/manageHelpers';

// Tipo de intervenção — alinhado com o SLAContract.interventionType e com
// o pedido do operador Petrotec: 3 categorias claras.
// Os antigos 'eventual' / 'wear' ficam marcados legacy e só aparecem se
// uma OT pré-existente os trouxer — para não perder dados históricos.
const INTERVENTION_TYPES = [
  { key: 'accident',   labelPt: 'Por acidente', labelEn: 'Accident',   color: '#dc2626' },
  { key: 'corrective', labelPt: 'Corretiva',    labelEn: 'Corrective', color: '#f59e0b' },
  { key: 'preventive', labelPt: 'Preventiva',   labelEn: 'Preventive', color: '#0ea5e9' },
  // Legacy — escondidos no select excepto se a OT já tem o valor gravado.
  { key: 'eventual',   labelPt: 'Eventual (legado)',   labelEn: 'Eventual (legacy)', color: '#94a3b8', legacy: true },
  { key: 'wear',       labelPt: 'P/Desgaste (legado)', labelEn: 'Wear (legacy)',     color: '#94a3b8', legacy: true },
];

// Tipo de contrato comercial — campo `contractType` (independente do
// `causeType` legado que afecta H.E.). Pedido do operador para classificar
// a OT do ponto de vista contratual logo na criação.
const CONTRACT_TYPES = [
  { key: 'avenca',        labelPt: 'Avença',         labelEn: 'Avença' },
  { key: 'extra_avenca',  labelPt: 'Extra avença',   labelEn: 'Extra avença' },
  { key: 'com_contrato',  labelPt: 'Com contrato',   labelEn: 'With contract' },
  { key: 'sem_contrato',  labelPt: 'Sem contrato',   labelEn: 'Without contract' },
];

// O OrderForm é montado num Portal MUI (Dialog) com z-index alto. O
// popover do Select, por padrão, vai para outro Portal e às vezes fica
// por baixo do diálogo, parecendo "vazio". Forçar `disablePortal: true`
// faz o menu abrir dentro do contexto do próprio Dialog, sem conflito
// de z-index. `getContentAnchorEl: null` é necessário em MUI v4 para que
// o `anchorOrigin` faça efeito (sem aviso de runtime).
const selectMenuProps = {
  disablePortal: true,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: { style: { maxHeight: 320 } },
};

const useStyles = makeStyles(() => ({
  wrap: {
    padding: '12px 14px', marginBottom: 10, borderRadius: 10,
    background: '#fff',
    border: '1px solid #fee2e2', borderLeft: '3px solid #dc2626',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  title: {
    fontSize: '0.78rem', fontWeight: 800, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 6, flex: 1,
  },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 },
  rowEq: { display: 'flex', flexDirection: 'column', gap: 6 },
  helpText: {
    fontSize: '0.66rem', color: '#64748b',
  },
  slaPill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 8px', borderRadius: 999,
    fontWeight: 800, fontSize: '0.72rem',
  },
  eqChip: {
    background: 'rgba(220,38,38,0.10)', color: '#dc2626',
    fontSize: '0.65rem', fontWeight: 700,
    margin: 2,
  },
  emptyEq: {
    padding: '8px 10px', borderRadius: 8,
    background: '#fff', border: '1px dashed #e2e8f0',
    fontSize: '0.7rem', color: '#94a3b8',
  },
}));

function formatHoursLabel(h) {
  if (h < 24) return `${h}h`;
  if (h % 24 === 0) return `${h / 24}d`;
  return `${h}h`;
}

// Metadata visual dos tiers de SLA — usado pelo painel SLA do cliente
// para destacar o nível ("Emergente" a vermelho, etc.) consistente com
// a UI de gestão (Manage/SLAContractsView).
const TIER_META = {
  emergent:  { labelPt: 'Emergente',  labelEn: 'Emergent',  color: '#dc2626', bg: '#fee2e2' },
  urgent:    { labelPt: 'Urgente',    labelEn: 'Urgent',    color: '#ea580c', bg: '#ffedd5' },
  normal:    { labelPt: 'Normal',     labelEn: 'Normal',    color: '#0ea5e9', bg: '#e0f2fe' },
  scheduled: { labelPt: 'Programada', labelEn: 'Scheduled', color: '#16a34a', bg: '#dcfce7' },
};

const INTERV_LABEL_PT = {
  accident:   'Por acidente',
  corrective: 'Corretiva',
  preventive: 'Preventiva',
  eventual:   'Eventual (legado)',
  wear:       'P/Desgaste (legado)',
};

function AOPetrotecOrderSection({ aoForm, orderId = null, onChange, onSiteSelected, onSlaSuggested, onSlaResolutionSuggested, lang = 'pt' }) {
  const classes = useStyles();
  const pt = lang === 'pt';
  const { token, clientExternalId, clientSettings } = useContext(AuthContext);

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [slas, setSlas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [interventionsHydrated, setInterventionsHydrated] = useState(false);

  // SLA actual = vigora hoje E corresponde ao cliente final × tipo
  const nowMs = Date.now();
  const slaActiveAt = (s, when) => {
    if (!s.isActive) return false;
    const from = new Date(s.validFrom).getTime();
    const to = s.validTo ? new Date(s.validTo).getTime() : null;
    return when >= from && (!to || when <= to);
  };
  const currentSla = slas.find(s =>
    s.finalCustomerId === aoForm.finalCustomerId
    && s.interventionType === aoForm.interventionType
    && slaActiveAt(s, nowMs)
  );

  // SLAs activos deste cliente final agrupados por tipo de intervenção —
  // alimentam o painel "SLA do cliente" que ajuda o operador a ver as
  // condições contratuais antes de classificar a OT.
  const slasByType = useMemo(() => {
    const m = {};
    for (const s of slas) {
      if (s.finalCustomerId !== aoForm.finalCustomerId) continue;
      if (!slaActiveAt(s, nowMs)) continue;
      const prev = m[s.interventionType];
      // Quando há vários histórias por tipo (ex.: revisão contratual),
      // mantemos o mais recente (validFrom mais alto).
      if (!prev || new Date(s.validFrom) > new Date(prev.validFrom)) {
        m[s.interventionType] = s;
      }
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slas, aoForm.finalCustomerId]);

  // Auto-popular slaHours no form raiz sempre que muda o SLA actual.
  // Apenas sugere — o operador continua a poder fazer override no campo.
  useEffect(() => {
    if (!onSlaSuggested) return;
    if (!currentSla?.targetHours) return;
    onSlaSuggested(currentSla.targetHours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSla?.id, currentSla?.targetHours]);

  // Mesma lógica para o 2º SLA (Resolução). Só sugere quando o
  // contrato tem targetResolutionHours; caso contrário fica null e
  // o chip Resolução nem aparece na OT.
  useEffect(() => {
    if (!onSlaResolutionSuggested) return;
    if (!currentSla) return;
    onSlaResolutionSuggested(currentSla.targetResolutionHours || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSla?.id, currentSla?.targetResolutionHours]);

  // Carregar clientes finais activos (sempre que o modo AO está visível).
  const fetchCustomers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/planner-api/final-customers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(pt ? 'Falha a carregar clientes finais.' : 'Failed to load customers.');
    } finally {
      setLoading(false);
    }
  }, [token, pt]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // Carregar postos quando muda o cliente final
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!aoForm.finalCustomerId || !token) {
        setSites([]); return;
      }
      try {
        const r = await fetch(
          `/planner-api/customer-sites?finalCustomerId=${aoForm.finalCustomerId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setSites(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setSites([]);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [aoForm.finalCustomerId, token]);

  // Em modo edit, o operador pode ter editado a OT (ou alterado o posto na
  // gestão de Postos) e a delivery da OT pode ter ficado dessincronizada
  // do posto seleccionado. Quando os sites carregam e o `siteId` está
  // setado, disparamos `onSiteSelected` UMA ÚNICA VEZ para resync. Isto
  // garante que mudar o posto e gravar reflete na rota/despacho.
  const [siteSyncedForOrder, setSiteSyncedForOrder] = useState(null);
  useEffect(() => {
    if (!orderId) return;
    if (siteSyncedForOrder === orderId) return;
    if (!aoForm.siteId || sites.length === 0) return;
    const site = sites.find(s => s.id === aoForm.siteId);
    if (!site) return;
    setSiteSyncedForOrder(orderId);
    if (onSiteSelected) {
      onSiteSelected({
        locationName: site.name,
        address: site.address || '',
        coordinates: (site.lat != null && site.lng != null)
          ? [Number(site.lng), Number(site.lat)]
          : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, aoForm.siteId, sites]);

  // Carregar equipamentos quando muda o posto
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!aoForm.siteId || !token) {
        setEquipments([]); return;
      }
      try {
        const r = await fetch(
          `/planner-api/site-equipments?siteId=${aoForm.siteId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setEquipments(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setEquipments([]);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [aoForm.siteId, token]);

  // Em modo edição, hidratar os equipmentIds da OT a partir das intervenções
  // já registadas (o backend não devolve isso no payload base). Corre uma
  // única vez por orderId — depois o operador pode mexer livremente.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!orderId || !token || interventionsHydrated) return;
      try {
        const r = await fetch(`/planner-api/transport-orders/${orderId}/equipments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const ids = Array.isArray(data) ? data.map(i => i.equipmentId).filter(Boolean) : [];
        const problems = {};
        if (Array.isArray(data)) {
          for (const it of data) {
            if (it.equipmentId && it.problemDescription) {
              problems[it.equipmentId] = String(it.problemDescription);
            }
          }
        }
        if (ids.length > 0) {
          // Só hidrata se ainda não houver selecções (evita esmagar input do operador)
          if (!aoForm.equipmentIds || aoForm.equipmentIds.length === 0) {
            onChange({
              ...aoForm,
              equipmentIds: ids,
              equipmentProblems: { ...(aoForm.equipmentProblems || {}), ...problems },
            });
          }
        }
      } catch (e) { /* swallow — não bloqueia o fluxo */ }
      finally {
        if (!cancelled) setInterventionsHydrated(true);
      }
    }
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, token, interventionsHydrated]);

  // Carregar SLAs do cliente final assim que estiver escolhido
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!aoForm.finalCustomerId || !token) {
        setSlas([]); return;
      }
      try {
        const r = await fetch(
          `/planner-api/sla-contracts?finalCustomerId=${aoForm.finalCustomerId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setSlas(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setSlas([]);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [aoForm.finalCustomerId, token]);

  // Só renderiza para tenants Petrotec (PT '20' e AO '918'). Outros
  // tenants (não-Petrotec) não vêem este bloco — fluxo original intocado.
  if (!isPetrotecTenant(clientExternalId, clientSettings)) return null;

  function handleCustomerChange(v) {
    const fcId = v ? parseInt(v) : '';
    onChange({
      ...aoForm,
      finalCustomerId: fcId || '',
      siteId: '',           // limpa selecções dependentes
      equipmentIds: [],
    });
  }

  function handleSiteChange(v) {
    const siteIdNum = v ? parseInt(v) : '';
    onChange({
      ...aoForm,
      siteId: siteIdNum || '',
      equipmentIds: [],
    });
    // Auto-popular a morada de entrega se o posto tiver coords/morada
    if (siteIdNum && onSiteSelected) {
      const site = sites.find(s => s.id === siteIdNum);
      if (site) {
        onSiteSelected({
          locationName: site.name,
          address: site.address || '',
          coordinates: (site.lat != null && site.lng != null)
            ? [Number(site.lng), Number(site.lat)]
            : null,
        });
      }
    }
  }

  function handleTypeChange(v) {
    onChange({ ...aoForm, interventionType: v || '' });
  }

  function handleContractTypeChange(v) {
    onChange({ ...aoForm, contractType: v || '' });
  }

  function toggleEquipment(eqId) {
    const ids = Array.isArray(aoForm.equipmentIds) ? aoForm.equipmentIds : [];
    const i = ids.indexOf(eqId);
    const next = i >= 0 ? ids.filter(x => x !== eqId) : [...ids, eqId];
    // Ao remover um equipamento, limpa também o motivo respectivo —
    // evita que problemas órfãos sigam no payload.
    const problems = { ...(aoForm.equipmentProblems || {}) };
    if (i >= 0) delete problems[eqId];
    onChange({ ...aoForm, equipmentIds: next, equipmentProblems: problems });
  }

  function updateEquipmentProblem(eqId, text) {
    const problems = { ...(aoForm.equipmentProblems || {}) };
    if (text) problems[eqId] = text;
    else delete problems[eqId];
    onChange({ ...aoForm, equipmentProblems: problems });
  }

  return (
    <Box className={classes.wrap}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          <BuildIcon style={{ fontSize: 16, color: '#dc2626' }} />
          {pt ? 'Detalhes da intervenção' : 'Intervention details'}
        </Typography>
        {currentSla && (() => {
          const tm = currentSla.tier && TIER_META[currentSla.tier];
          return (
            <Tooltip title={pt
              ? `SLA em vigor — Resposta ${currentSla.targetHours}h${currentSla.targetResolutionHours ? ` · Resolução ${currentSla.targetResolutionHours}h` : ''}`
              : `Active SLA — Response ${currentSla.targetHours}h${currentSla.targetResolutionHours ? ` · Resolution ${currentSla.targetResolutionHours}h` : ''}`}>
              <Box className={classes.slaPill} style={{
                background: tm ? tm.bg : 'rgba(34,197,94,0.12)',
                color: tm ? tm.color : '#16a34a',
                border: tm ? `1px solid ${tm.color}33` : undefined,
              }}>
                <TimerIcon style={{ fontSize: 12 }} />
                {tm && <span style={{ fontWeight: 800 }}>{pt ? tm.labelPt : tm.labelEn} · </span>}
                {formatHoursLabel(currentSla.targetHours)}
                {currentSla.targetResolutionHours != null && (
                  <span style={{ opacity: 0.85 }}> / {formatHoursLabel(currentSla.targetResolutionHours)}</span>
                )}
                {currentSla.targetKm != null && <span> · {currentSla.targetKm}km</span>}
              </Box>
            </Tooltip>
          );
        })()}
        <Tooltip title={pt ? 'Recarregar listas' : 'Refresh'}>
          <IconButton size="small" onClick={fetchCustomers}>
            <RefreshIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Box style={{
          padding: '6px 10px', borderRadius: 6, fontSize: '0.7rem',
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
        }}>
          {error}
        </Box>
      )}

      {/* Debug strip — mostra estado actual da lista. Útil enquanto estamos
          a estabilizar este fluxo; remover quando passar a produção. */}
      <Box style={{
        fontSize: '0.62rem', color: '#94a3b8',
        padding: '0 2px', display: 'flex', gap: 10,
      }}>
        <span>{customers.length} {pt ? 'clientes carregados' : 'customers loaded'}</span>
        {aoForm.finalCustomerId && <span>· FC = {aoForm.finalCustomerId}</span>}
        {sites.length > 0 && <span>· {sites.length} {pt ? 'postos' : 'sites'}</span>}
        {equipments.length > 0 && <span>· {equipments.length} {pt ? 'equipamentos' : 'equipments'}</span>}
      </Box>

      {loading && customers.length === 0 ? (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.7rem', color: '#64748b' }}>
          <CircularProgress size={14} style={{ color: '#dc2626' }} />
          {pt ? 'A carregar…' : 'Loading…'}
        </Box>
      ) : customers.length === 0 ? (
        <Box style={{
          padding: '8px 10px', borderRadius: 6, fontSize: '0.72rem',
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
        }}>
          {pt
            ? 'Ainda não há clientes finais. Cria primeiro em "Clientes Finais (Angola)" no menu ⋮.'
            : 'No final customers yet. Create one in the menu first.'}
        </Box>
      ) : (
        <>
          <Box className={classes.row3}>
            <FormControl size="small" variant="outlined">
              <InputLabel id="ao-fc-label">{pt ? 'Cliente final' : 'Final customer'}</InputLabel>
              <Select
                labelId="ao-fc-label"
                label={pt ? 'Cliente final' : 'Final customer'}
                value={aoForm.finalCustomerId ? String(aoForm.finalCustomerId) : ''}
                onChange={e => handleCustomerChange(e.target.value)}
                MenuProps={selectMenuProps}
              >
                <MenuItem value=""><em>— {pt ? 'Nenhum' : 'None'} —</em></MenuItem>
                {customers.map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" variant="outlined" disabled={!aoForm.finalCustomerId}>
              <InputLabel id="ao-site-label">{pt ? 'Posto' : 'Site'}</InputLabel>
              <Select
                labelId="ao-site-label"
                label={pt ? 'Posto' : 'Site'}
                value={aoForm.siteId ? String(aoForm.siteId) : ''}
                onChange={e => handleSiteChange(e.target.value)}
                MenuProps={selectMenuProps}
              >
                <MenuItem value=""><em>— {pt ? 'Nenhum' : 'None'} —</em></MenuItem>
                {sites.map(s => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" variant="outlined" disabled={!aoForm.finalCustomerId}>
              <InputLabel id="ao-type-label">{pt ? 'Tipo' : 'Type'}</InputLabel>
              <Select
                labelId="ao-type-label"
                label={pt ? 'Tipo' : 'Type'}
                value={aoForm.interventionType || ''}
                onChange={e => handleTypeChange(e.target.value)}
                MenuProps={selectMenuProps}
              >
                <MenuItem value=""><em>— {pt ? 'Nenhum' : 'None'} —</em></MenuItem>
                {/* Esconder 'corrective' legado, excepto se já está
                    seleccionado nesta OT (modo edit) */}
                {INTERVENTION_TYPES
                  .filter(t => !t.legacy || aoForm.interventionType === t.key)
                  .map(t => (
                    <MenuItem key={t.key} value={t.key}>
                      {pt ? t.labelPt : t.labelEn}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          {/* Painel SLA do cliente — mostra todos os SLAs activos do
              FinalCustomer escolhido, com destaque para o que se aplica
              ao Tipo seleccionado. Faltar SLA num tipo é informação útil
              (UI sinaliza). Sem SLAs não rendiriza nada. */}
          {aoForm.finalCustomerId && Object.keys(slasByType).length > 0 && (
            <Box style={{
              padding: '10px 12px', borderRadius: 10,
              background: '#f0fdfa', border: '1px solid #99f6e4',
            }}>
              <Typography style={{
                fontSize: '0.7rem', fontWeight: 800, color: '#115e59',
                textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
              }}>
                {pt ? 'SLA do cliente' : 'Customer SLA'}
              </Typography>
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {['accident', 'corrective', 'preventive', 'eventual', 'wear']
                  .filter(t => slasByType[t])
                  .map(t => {
                    const s = slasByType[t];
                    const tm = s.tier && TIER_META[s.tier];
                    const isCurrent = aoForm.interventionType === t;
                    return (
                      <Box key={t} style={{
                        padding: '4px 10px', borderRadius: 8,
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: tm ? tm.bg : '#fff',
                        color: tm ? tm.color : '#0f172a',
                        border: isCurrent
                          ? `2px solid ${tm ? tm.color : '#0ea5e9'}`
                          : '1px solid #e2e8f0',
                        fontSize: '0.72rem', fontWeight: 700,
                        boxShadow: isCurrent ? '0 2px 6px rgba(15,118,110,0.18)' : undefined,
                      }}>
                        {INTERV_LABEL_PT[t] || t}
                        {tm && <span style={{ fontWeight: 800 }}> · {pt ? tm.labelPt : tm.labelEn}</span>}
                        <span style={{ opacity: 0.85 }}>· {formatHoursLabel(s.targetHours)}</span>
                        {s.targetResolutionHours != null && (
                          <span style={{ opacity: 0.7, fontWeight: 600 }}>
                            / {formatHoursLabel(s.targetResolutionHours)}
                          </span>
                        )}
                        {s.targetKm != null && (
                          <span style={{ opacity: 0.7, fontWeight: 600 }}>· {s.targetKm}km</span>
                        )}
                        {isCurrent && (
                          <span style={{ fontSize: '0.6rem', marginLeft: 4, opacity: 0.75 }}>
                            ◄ {pt ? 'aplicado' : 'applied'}
                          </span>
                        )}
                      </Box>
                    );
                  })}
              </Box>
              {aoForm.interventionType && !slasByType[aoForm.interventionType] && (
                <Typography style={{
                  fontSize: '0.66rem', color: '#92400e', marginTop: 6,
                }}>
                  {pt
                    ? '⚠ Sem SLA definido para este tipo neste cliente. Define um em Gestão → SLAs.'
                    : '⚠ No SLA defined for this type on this customer. Add one in Manage → SLAs.'}
                </Typography>
              )}
            </Box>
          )}

          {/* Tipo de contrato comercial — campo principal de classificação
              pelo operador. Campo separado do `causeType` legacy (que
              continua a influenciar a contagem H.N./H.E. abaixo). */}
          <Box style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8, alignItems: 'center' }}>
            <FormControl size="small" variant="outlined" disabled={!aoForm.finalCustomerId}>
              <InputLabel id="ao-contract-label">{pt ? 'Tipo de contrato' : 'Contract type'}</InputLabel>
              <Select
                labelId="ao-contract-label"
                label={pt ? 'Tipo de contrato' : 'Contract type'}
                value={aoForm.contractType || ''}
                onChange={e => handleContractTypeChange(e.target.value)}
                MenuProps={selectMenuProps}
              >
                <MenuItem value=""><em>— {pt ? 'Por classificar' : 'Unclassified'} —</em></MenuItem>
                {CONTRACT_TYPES.map(c => (
                  <MenuItem key={c.key} value={c.key}>{pt ? c.labelPt : c.labelEn}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography className={classes.helpText}>
              {pt
                ? 'Classifica a OT em termos contratuais (independente do cálculo H.E.).'
                : 'Classifies the OT contractually (independent from overtime calculation).'}
            </Typography>
          </Box>

          {/* Causa (cálculo H.E.) — campo legacy escondido do operador.
              Continua a influenciar o BM, mas é DERIVADO automaticamente
              do "Tipo de contrato" no backend (Avença/Com contrato →
              covered; Extra avença/Sem contrato → external). Para overrides
              pontuais, usar o endpoint directamente. */}
          {(aoForm.contractType === 'extra_avenca' || aoForm.contractType === 'sem_contrato') && (
            <Typography className={classes.helpText} style={{ color: '#92400e' }}>
              {pt
                ? '⚠ Esta classificação debita Sáb/Dom/Feriados como H.E. no BM.'
                : '⚠ This classification bills Sat/Sun/Holidays as overtime in the BM.'}
            </Typography>
          )}
          {(aoForm.contractType === 'avenca' || aoForm.contractType === 'com_contrato') && (
            <Typography className={classes.helpText} style={{ color: '#15803d' }}>
              {pt
                ? '✓ Sáb/Dom/Feriados não são debitados ao cliente (regra da avença).'
                : '✓ Sat/Sun/Holidays not billed to the customer (contract rule).'}
            </Typography>
          )}

          {aoForm.siteId && (
            <Box className={classes.rowEq}>
              <Typography className={classes.helpText}>
                {pt
                  ? 'Equipamentos a intervir (clica para seleccionar — depois podes adicionar o motivo da intervenção):'
                  : 'Equipments to intervene (click to select — then you can add the intervention reason):'}
              </Typography>
              {equipments.length === 0 ? (
                <Box className={classes.emptyEq}>
                  {pt
                    ? 'Este posto ainda não tem equipamentos registados. Podes guardar a OT sem associar nenhum.'
                    : 'This site has no registered equipments yet. You can save the OT without any.'}
                </Box>
              ) : (
                <>
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {equipments.map(eq => {
                      const selected = (aoForm.equipmentIds || []).includes(eq.id);
                      const label = `${eq.subtype}${eq.positionLabel ? ` · ${eq.positionLabel}` : ''}${eq.assetNumber ? ` (#${eq.assetNumber})` : ''}`;
                      return (
                        <Chip
                          key={eq.id}
                          label={label}
                          size="small"
                          onClick={() => toggleEquipment(eq.id)}
                          className={classes.eqChip}
                          style={{
                            background: selected ? '#dc2626' : 'rgba(220,38,38,0.10)',
                            color: selected ? '#fff' : '#dc2626',
                            cursor: 'pointer',
                          }}
                        />
                      );
                    })}
                  </Box>

                  {/* Hint quando o operador ainda não escolheu nenhum
                      equipamento — esclarece que o motivo só fica disponível
                      depois de seleccionar (descoberta de feature). */}
                  {(aoForm.equipmentIds || []).length === 0 && (
                    <Box style={{
                      marginTop: 6, padding: '8px 12px',
                      background: '#fff7ed', borderRadius: 8, border: '1px dashed #fdba74',
                      fontSize: '0.7rem', color: '#9a3412',
                    }}>
                      {pt
                        ? '💡 Selecciona acima os equipamentos onde vai intervir. Depois de seleccionados, podes descrever o motivo (ex.: "Bomba 2 a verter").'
                        : '💡 Pick the equipments to intervene above. Once selected, you can describe the reason (ex.: "Pump 2 leaking").'}
                    </Box>
                  )}

                  {/* Motivo da intervenção por equipamento — só aparece para
                      os equipamentos seleccionados. O motorista pode editar
                      no terreno se o diagnóstico mudar (ex.: cliente diz
                      Bomba 2, mas é a Bomba 3). */}
                  {(aoForm.equipmentIds || []).length > 0 && (
                    <Box style={{
                      display: 'flex', flexDirection: 'column', gap: 6,
                      marginTop: 6, padding: '8px 10px',
                      background: '#fff', borderRadius: 8, border: '1px solid #fee2e2',
                    }}>
                      <Typography className={classes.helpText} style={{ color: '#991b1b', fontWeight: 600 }}>
                        {pt ? 'Motivo da intervenção por equipamento (opcional)' : 'Intervention reason per equipment (optional)'}
                      </Typography>
                      {(aoForm.equipmentIds || []).map(eqId => {
                        const eq = equipments.find(e => e.id === eqId);
                        if (!eq) return null;
                        const label = `${eq.subtype}${eq.positionLabel ? ` · ${eq.positionLabel}` : ''}`;
                        const value = (aoForm.equipmentProblems || {})[eqId] || '';
                        return (
                          <Box key={eqId} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'center' }}>
                            <Typography style={{
                              fontSize: '0.72rem', fontWeight: 700, color: '#475569',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }} title={label}>
                              {label}
                            </Typography>
                            <input
                              type="text"
                              maxLength={1000}
                              placeholder={pt ? 'Ex.: Bomba a verter, totalizador parado…' : 'Ex.: Leaking, totalizer stuck…'}
                              value={value}
                              onChange={e => updateEquipmentProblem(eqId, e.target.value)}
                              style={{
                                width: '100%', padding: '6px 10px',
                                fontSize: '0.78rem', color: '#0f172a',
                                background: '#f8fafc', border: '1px solid #e2e8f0',
                                borderRadius: 6, outline: 'none', fontFamily: 'inherit',
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default AOPetrotecOrderSection;
